import React, { useMemo } from 'react';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';

// Универсальный пейджинатор. Окно из 1 + первой/последней + 2 соседей с эллипсами.
const buildRange = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages = new Set([1, total, current, current - 1, current + 1]);
  const arr = [...pages].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);

  const result = [];
  for (let i = 0; i < arr.length; i++) {
    if (i > 0 && arr[i] - arr[i - 1] > 1) result.push('…');
    result.push(arr[i]);
  }
  return result;
};

const Pagination = ({ page, pageCount, onChange, className = '' }) => {
  const range = useMemo(() => buildRange(page, pageCount), [page, pageCount]);

  if (pageCount <= 1) return null;

  const go = (n) => {
    const next = Math.min(pageCount, Math.max(1, n));
    if (next !== page) onChange(next);
  };

  return (
    <nav
      className={`flex flex-wrap items-center justify-center gap-1 ${className}`}
      aria-label="Pagination"
    >
      <button
        onClick={() => go(page - 1)}
        disabled={page <= 1}
        className="inline-flex h-9 items-center gap-1 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
        aria-label="Предыдущая"
      >
        <ChevronLeft className="w-4 h-4" />
        <span className="hidden sm:inline">Назад</span>
      </button>

      {range.map((p, i) =>
        p === '…' ? (
          <span
            key={`dots-${i}`}
            className="inline-flex h-9 w-9 items-center justify-center text-gray-400"
          >
            <MoreHorizontal className="w-4 h-4" />
          </span>
        ) : (
          <button
            key={p}
            onClick={() => go(p)}
            className={`inline-flex h-9 min-w-9 items-center justify-center rounded-xl border px-3 text-sm tabular-nums transition ${
              p === page
                ? 'border-indigo-600 bg-indigo-600 text-white shadow-sm'
                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
            aria-current={p === page ? 'page' : undefined}
          >
            {p}
          </button>
        )
      )}

      <button
        onClick={() => go(page + 1)}
        disabled={page >= pageCount}
        className="inline-flex h-9 items-center gap-1 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
        aria-label="Следующая"
      >
        <span className="hidden sm:inline">Далее</span>
        <ChevronRight className="w-4 h-4" />
      </button>
    </nav>
  );
};

export default Pagination;
